import { useAtom, useAtomValue } from 'jotai'
import { JSX, useRef, useState } from 'react'
import { MediaAsset } from '../../views'
import { campaignDetailsBrandingAtom, campaignDetailsLogoUploadAtom } from '../state'

export type UseLogoUploadReturn = {
	/** Trigger the file input to open the file picker */
	triggerUpload: () => void
	/** Whether a logo upload is currently in progress */
	isUploading: boolean
	/** Hidden file input element - must be rendered in the component tree */
	fileInput: JSX.Element
}

/**
 * Hook for handling logo uploads in campaign editor context.
 * Provides file input, upload trigger, and loading state management.
 * Automatically updates campaignDetailsBrandingAtom on successful upload.
 */
export function useLogoUpload(): UseLogoUploadReturn {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const onLogoUpload = useAtomValue(campaignDetailsLogoUploadAtom)
	const [branding, setBranding] = useAtom(campaignDetailsBrandingAtom)
	const [isUploading, setIsUploading] = useState(false)

	const handleFileChange = (file?: File) => {
		if (!file || !branding) { return }

		setIsUploading(true)
		onLogoUpload?.(
			file,
			(asset: MediaAsset) => {
				setIsUploading(false)
				setBranding({
					...branding,
					logoId: asset.id,
					logo: asset,
				})
			},
		)
	}

	const triggerUpload = () => {
		fileInputRef.current?.click()
	}

	const fileInput = (
		<input
			ref={fileInputRef}
			type="file"
			accept="image/*"
			hidden
			onChange={(e) => {
				const file = e.target.files?.[0]
				// Reset immediately so the same file can be re-selected
				e.target.value = ''
				handleFileChange(file)
			}}
		/>
	)

	return { triggerUpload, isUploading, fileInput }
}
